import { useCallback, useRef, useState } from 'react';

import { ProductsSequenceProduct } from 'common/contracts';

import {
  appendProduct,
  replaceWithSingleProduct,
  seedSingleFromMulti
} from './usePopupProducts.utils';

export type ProductEntry = ProductsSequenceProduct & { id: string };

interface IUsePopupProductsReturn {
  productsList: ProductEntry[];
  addProduct: (product: ProductsSequenceProduct) => void;
  deleteProduct: (id: string) => void;
  updateProduct: (id: string, product: ProductsSequenceProduct) => void;
  reorderProducts: (newOrder: ProductEntry[]) => void;
  setMultiProducts: (products: ProductEntry[]) => void;
  setSingleProducts: (products: ProductEntry[]) => void;
  peekActiveListForMode: (nextIsSingle: boolean) => ProductEntry[];
}

/**
 * Manages two independent product lists for popup forms - one for multi-product modes
 * (Store Refresh, Daily Bonus) and one for single-product mode (Post Purchase) - so
 * switching trigger actions never loses the user's data. On the first false to true
 * transition with empty single state, single is seeded from the multi MAIN product.
 *
 * @example
 * const { productsList, addProduct } = usePopupProducts(true);
 */
export const usePopupProducts = (
  isSingleProductMode: boolean
): IUsePopupProductsReturn => {
  const [multiProducts, setMultiProductsState] = useState<ProductEntry[]>([]);
  const [singleProducts, setSingleProductsState] = useState<ProductEntry[]>([]);
  const prevSingleMode = useRef(isSingleProductMode);

  if (
    isSingleProductMode &&
    !prevSingleMode.current &&
    singleProducts.length === 0 &&
    multiProducts.length > 0
  ) {
    setSingleProductsState(seedSingleFromMulti(multiProducts));
  }
  prevSingleMode.current = isSingleProductMode;

  const productsList = isSingleProductMode ? singleProducts : multiProducts;

  const addProduct = useCallback(
    (newProduct: ProductsSequenceProduct): void => {
      if (isSingleProductMode) {
        setSingleProductsState(replaceWithSingleProduct(newProduct));
      } else {
        setMultiProductsState((prev) => appendProduct(prev, newProduct));
      }
    },
    [isSingleProductMode]
  );

  const deleteProduct = useCallback(
    (id: string): void => {
      if (isSingleProductMode) {
        setSingleProductsState((prev) => prev.filter((p) => p.id !== id));
      } else {
        setMultiProductsState((prev) => prev.filter((p) => p.id !== id));
      }
    },
    [isSingleProductMode]
  );

  const updateProduct = useCallback(
    (id: string, newProductData: ProductsSequenceProduct): void => {
      const updater = (prev: ProductEntry[]): ProductEntry[] =>
        prev.map((p) =>
          p.id === id ? { ...p, ...newProductData } : p
        );
      if (isSingleProductMode) {
        setSingleProductsState(updater);
      } else {
        setMultiProductsState(updater);
      }
    },
    [isSingleProductMode]
  );

  const reorderProducts = useCallback(
    (newOrder: ProductEntry[]): void => {
      if (isSingleProductMode) {
        setSingleProductsState(newOrder);
      } else {
        setMultiProductsState(newOrder);
      }
    },
    [isSingleProductMode]
  );

  const setMultiProducts = useCallback((products: ProductEntry[]): void => {
    setMultiProductsState(products);
  }, []);

  const setSingleProducts = useCallback((products: ProductEntry[]): void => {
    setSingleProductsState(products);
  }, []);

  const peekActiveListForMode = useCallback(
    (nextIsSingle: boolean): ProductEntry[] => {
      if (!nextIsSingle) return multiProducts;
      if (singleProducts.length > 0) return singleProducts;
      if (multiProducts.length > 0) return seedSingleFromMulti(multiProducts);
      return [];
    },
    [multiProducts, singleProducts]
  );

  return {
    productsList,
    addProduct,
    deleteProduct,
    updateProduct,
    reorderProducts,
    setMultiProducts,
    setSingleProducts,
    peekActiveListForMode
  };
};
